import React from "react";
import { Button } from "../ui/Button";
import { useI18n } from "../../i18n";
import ComplianceFormCard, { ComplianceFormCategory } from "./ComplianceForm";
import ExtendedQuestionnaire, {
  ExtendedQuestionnaireAnswer,
  ExtendedQuestionnaireBlock,
} from "./ExtendedQuestionnaire";
import type { Iso42001UiState } from "../../utils/iso42001Questionnaire";

type Props = {
  mode: "QUICK" | "SELF_ASSESSMENT";
  quickState: Iso42001UiState;
  fullState: Iso42001UiState;
  visibleFormCategories: ComplianceFormCategory[];
  quickAnswers: Record<string, Record<string, number>>;
  expandedCategoryIds: string[];
  onToggleCategory: (categoryId: string) => void;
  onQuickAnswer: (categoryId: string, questionId: string, value: number) => void;
  onSaveQuick: () => Promise<boolean>;
  getQuestionLabel: (category: ComplianceFormCategory, question: ComplianceFormCategory["questions"][number]) => string;
  fullBlocks: ExtendedQuestionnaireBlock[];
  fullAnswers: Record<string, ExtendedQuestionnaireAnswer>;
  fullProgress: { answered: number; total: number };
  onFullAnswer: (questionId: string, answer: ExtendedQuestionnaireAnswer) => void;
  onSaveFull: (data: {
    answers: Record<string, ExtendedQuestionnaireAnswer>;
    blockStats: Record<string, { yes: number; no: number; partial: number; n_a: number }>;
    overallStats: { yes: number; no: number; partial: number; n_a: number; total: number };
  }) => Promise<void>;
  onRetry: () => void;
  disabled?: boolean;
};

function StateNotice({
  state,
  onRetry,
}: {
  state: Iso42001UiState;
  onRetry: () => void;
}) {
  const { t } = useI18n();
  if (state === "loading") {
    return (
      <div data-testid="iso42001-loading" className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        {t("compliance.forms.iso42001.loading", "Loading questionnaire…")}
      </div>
    );
  }
  if (state === "error") {
    return (
      <div data-testid="iso42001-error" className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <p>{t("compliance.forms.iso42001.loadError", "Failed to load the questionnaire.")}</p>
        <Button type="button" data-testid="iso42001-retry" className="mt-3" onClick={onRetry}>
          {t("compliance.forms.iso42001.retry", "Retry")}
        </Button>
      </div>
    );
  }
  if (state === "empty") {
    return (
      <div data-testid="iso42001-empty" className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        {t("compliance.forms.iso42001.emptyDefinition", "Questionnaire definition is unavailable")}
      </div>
    );
  }
  return null;
}

const Iso42001Questionnaire: React.FC<Props> = ({
  mode,
  quickState,
  fullState,
  visibleFormCategories,
  quickAnswers,
  expandedCategoryIds,
  onToggleCategory,
  onQuickAnswer,
  onSaveQuick,
  getQuestionLabel,
  fullBlocks,
  fullAnswers,
  fullProgress,
  onFullAnswer,
  onSaveFull,
  onRetry,
  disabled = false,
}) => {
  const { t } = useI18n();
  const [saveNotice, setSaveNotice] = React.useState<"success" | "error" | null>(null);
  const [saving, setSaving] = React.useState(false);

  if (mode === "QUICK") {
    const notice = <StateNotice state={quickState} onRetry={onRetry} />;
    if (notice && quickState !== "ready" && quickState !== "ready_empty") return notice;
    return (
      <div data-testid="iso42001-quick" className="space-y-4">
        {quickState === "ready_empty" ? (
          <p className="text-sm text-gray-500">
            {t("compliance.forms.iso42001.noResponses", "No responses yet.")}
          </p>
        ) : null}
        {visibleFormCategories.map((category) => (
          <div key={category.id} data-testid="iso42001-quick-category">
            <ComplianceFormCard
              category={category}
              isExpanded={expandedCategoryIds.includes(category.id)}
              answers={quickAnswers[category.id] || {}}
              onToggle={onToggleCategory}
              onAnswerChange={onQuickAnswer}
              getQuestionLabel={getQuestionLabel}
              disabled={disabled}
            />
          </div>
        ))}
        <div className="flex items-center gap-3">
          <Button
            type="button"
            data-testid="iso42001-quick-save"
            disabled={disabled || saving}
            onClick={async () => {
              if (saving) return;
              setSaving(true);
              setSaveNotice(null);
              try {
                const ok = await onSaveQuick();
                setSaveNotice(ok ? "success" : "error");
              } catch {
                setSaveNotice("error");
              } finally {
                setSaving(false);
              }
            }}
          >
            {t("compliance.forms.iso42001.save", "Save responses")}
          </Button>
          {saveNotice === "success" ? (
            <span className="text-sm text-green-700">{t("compliance.forms.iso42001.saveSuccess", "Responses saved")}</span>
          ) : null}
          {saveNotice === "error" ? (
            <span className="text-sm text-red-700">{t("compliance.forms.iso42001.saveError", "Failed to save responses")}</span>
          ) : null}
        </div>
      </div>
    );
  }

  const notice = <StateNotice state={fullState} onRetry={onRetry} />;
  if (notice && fullState !== "ready" && fullState !== "ready_empty") return notice;
  return (
    <div data-testid="iso42001-full" className="space-y-3">
      <p data-testid="iso42001-progress" className="text-sm text-gray-600">
        {t(
          "compliance.forms.iso42001.progress",
          `${fullProgress.answered}/${fullProgress.total}`,
          { answered: fullProgress.answered, total: fullProgress.total },
        )}
      </p>
      {fullState === "ready_empty" ? (
        <p className="text-sm text-gray-500">
          {t("compliance.forms.iso42001.noResponses", "No responses yet.")}
        </p>
      ) : null}
      <ExtendedQuestionnaire
        standardCode="ISO42001"
        questionnaireType="SELF_ASSESSMENT"
        blocks={fullBlocks}
        initialAnswers={fullAnswers}
        onAnswerChange={onFullAnswer}
        disabled={disabled || saving}
        onSave={async (data) => {
          if (saving) return;
          setSaving(true);
          setSaveNotice(null);
          try {
            await onSaveFull(data);
            setSaveNotice("success");
          } catch {
            setSaveNotice("error");
          } finally {
            setSaving(false);
          }
        }}
      />
      {saveNotice === "success" ? (
        <p className="text-sm text-green-700">{t("compliance.forms.iso42001.saveSuccess", "Responses saved")}</p>
      ) : null}
      {saveNotice === "error" ? (
        <p className="text-sm text-red-700">{t("compliance.forms.iso42001.saveError", "Failed to save responses")}</p>
      ) : null}
    </div>
  );
};

export default Iso42001Questionnaire;
